import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { staffAPI, confidentialAPI } from '../services/api';
import preOnboardingAPI from '../services/preOnboardingApi';
import toast from '../utils/toast';

// Staff list with filtering - returns full response for pagination
export const useStaffList = (params = {}, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  return useQuery({
    queryKey: ['staff', 'list', params],
    queryFn: async () => {
      const response = await staffAPI.getStaffList(params);
      return {
        results: response.data?.results || response.data || [],
        count: response.data?.count || 0,
        totalPages: response.data?.count ? Math.ceil(response.data.count / (params.page_size || 100)) : 1
      };
    },
    staleTime: 3 * 60 * 1000, // 3 minutes - staff data changes frequently
    enabled,
    ...queryOptions,
  });
};

// Individual staff member
export const useStaffMember = (staffId) => {
  return useQuery({
    queryKey: ['staff', 'detail', staffId],
    queryFn: async () => {
      const response = await staffAPI.getStaff(staffId);
      return response.data;
    },
    enabled: !!staffId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Current user profile
export const useCurrentUser = () => {
  return useQuery({
    queryKey: ['user', 'current'],
    queryFn: async () => {
      const response = await staffAPI.getCurrentUser();
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - user data rarely changes
    retry: (failureCount, error) => {
      // Don't retry auth failures
      if (error?.response?.status === 401) return false;
      return failureCount < 2;
    },
  });
};

// My profile data
export const useMyProfile = () => {
  return useQuery({
    queryKey: ['profile', 'me'],
    queryFn: async () => {
      const response = await staffAPI.getMyProfile();
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
  });
};

// My HR profile
export const useMyHRProfile = () => {
  return useQuery({
    queryKey: ['hr-profile', 'me'],
    queryFn: async () => {
      const response = await staffAPI.getMyHRProfile();
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      // 404 is expected if user doesn't have HR profile yet
      if (error?.response?.status === 404) return false;
      return failureCount < 2;
    },
  });
};

// Employee statistics for dashboard/management
export const useEmployeeStatistics = () => {
  return useQuery({
    queryKey: ['staff', 'statistics'],
    queryFn: async () => {
      const response = await staffAPI.getStaffStatistics();
      const data = response.data || {};
      
      // Transform backend field names to frontend expected field names
      return {
        total: data.total_staff || 0,
        newHires: data.new_hires_this_year || 0,
        currentYear: data.current_year || new Date().getFullYear(),  // Use backend year, fallback to client year
        probation: data.probation_staff || 0,
        avgTenure: data.avg_tenure_years || 0,
        departments: data.unique_departments || 0,
        systemAccounts: data.system_accounts || 0,
        totalIncludingInactive: data.total_including_inactive || 0,
        // Keep original data for debugging
        ...data
      };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - stats don't change frequently
  });
};

// Filtered employee statistics - accepts same parameters as staff list
export const useFilteredEmployeeStatistics = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['staff', 'statistics', 'filtered', params],
    queryFn: async () => {
      // Create a new API method that accepts parameters
      const response = await staffAPI.getFilteredStaffStatistics(params);
      const data = response.data || {};
      
      // Transform backend field names to frontend expected field names
      return {
        total: data.total_staff || 0,
        newHires: data.new_hires_this_year || 0,
        currentYear: data.current_year || new Date().getFullYear(),  // Use backend year, fallback to client year
        probation: data.probation_staff || 0,
        avgTenure: data.avg_tenure_years || 0,
        departments: data.unique_departments || 0,
        systemAccounts: data.system_accounts || 0,
        totalIncludingInactive: data.total_including_inactive || 0,
        // Keep original data for debugging
        ...data
      };
    },
    staleTime: 3 * 60 * 1000, // 3 minutes - filtered stats change more frequently
    enabled: options.enabled !== undefined ? options.enabled : true,
  });
};

// Staff mutations
export const useUpdateStaff = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ staffId, data }) => staffAPI.updateStaff(staffId, data),
    onSuccess: (data, variables) => {
      // Invalidate and refetch staff list
      queryClient.invalidateQueries({ queryKey: ['staff', 'list'] });
      // Update individual staff cache
      queryClient.setQueryData(['staff', 'detail', variables.staffId], data.data);
      toast.success('Staff updated successfully');
    },
    onError: (error) => {
      console.error('Error updating staff:', error);
      toast.error('Failed to update staff');
    },
  });
};

/**
 * Direct hire (HR "Add Employee") mutation.
 *
 * Posts the full onboarding payload to the direct-hire endpoint, which reuses
 * the materialisation engine to create a complete, audited employee. On
 * success it invalidates the staff list + statistics so the new hire (and the
 * Pre-Onboarding bucket for future starts) reflect immediately. Success/error
 * toasts are handled by the calling modal so it can surface the staff_id and
 * the 422 engine messages.
 */
export const useDirectHire = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => preOnboardingAPI.directHire(payload),
    onSuccess: () => {
      // Invalidate everything under ['staff'] (list + statistics + filtered).
      queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
};

export const useDeleteStaff = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (staffId) => staffAPI.deleteStaff(staffId),
    onSuccess: (data, staffId) => {
      // Remove from staff list cache
      queryClient.setQueryData(['staff', 'list'], (old) => {
        if (!old) return old;
        return old.filter(staff => staff.id !== staffId);
      });
      // Remove individual staff cache
      queryClient.removeQueries({ queryKey: ['staff', 'detail', staffId] });
      toast.success('Staff deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting staff:', error);
      toast.error('Failed to delete staff');
    },
  });
};

// Batch fetch HR profiles for multiple staff IDs
// Returns a Map for O(1) lookup by staff_id
export const useHRProfilesForStaff = (staffIds = []) => {
  return useQuery({
    queryKey: ['hr-profiles', 'batch', staffIds.length > 0 ? [...staffIds].sort().join(',') : 'empty'],
    queryFn: async () => {
      if (!staffIds || staffIds.length === 0) {
        return new Map();
      }

      try {
        // Use bulk filter: hrprofile_staff_id__in accepts comma-separated IDs
        const params = staffIds.length === 1
          ? { hrprofile_staff_id: staffIds[0] }
          : { 'hrprofile_staff_id__in': staffIds.join(',') };
        
        const response = await confidentialAPI.getHRProfiles(params);
        const profiles = response.data?.results || response.data || [];
        
        const profileMap = new Map();
        profiles.forEach(profile => {
          // Try both field names - API serializer adds 'staff_id' as convenience field
          const staffId = profile?.staff_id || profile?.hrprofile_staff_id;
          if (staffId) {
            profileMap.set(staffId, profile);
          }
        });
        
        return profileMap;
      } catch (error) {
        console.error('Error fetching HR profiles:', error);
        return new Map();
      }
    },
    enabled: staffIds && staffIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes - HR profile data changes infrequently
    retry: (failureCount, error) => {
      // Don't retry 404s (profile doesn't exist) or 403s (permission denied)
      if (error?.response?.status === 404 || error?.response?.status === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });
};
